import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Creatable, Drawer, Input, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateGenericContent } from '../hooks/useCreateGenericContent'
import {
  NewGenericContentForm,
  NewGenericContentSchema,
} from '../schemas/NewGenericContentSchema'

export function DrawerNewGenericContent() {
  const { t } = useTranslation(['settings', 'common'])
  const { close } = useDrawer('drawer-new-generic-content')
  const hookForm = useForm<NewGenericContentForm>({
    resolver: zodResolver(NewGenericContentSchema()),
  })
  const { handleCreateGenericContent, isCreatingGenericContent } =
    useCreateGenericContent()

  const handleSubmit = async (data: NewGenericContentForm) => {
    await handleCreateGenericContent(data).then(() => {
      hookForm.reset()
      close()
    })
  }

  const handleCancel = () => {
    hookForm.reset({
      tagContent: '',
      fieldKey: '',
      fieldValue: '',
      contentType: [],
      relativeKeys: [],
      searchTags: [],
      infoMessage: '',
    })
    close()
  }

  return (
    <Drawer.Root id="drawer-new-generic-content">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('generic_content.drawer_new_generic_content.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('generic_content.drawer_new_generic_content.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Base
              name="tagContent"
              label={t(
                'generic_content.drawer_new_generic_content.fields.tag_content.label',
              )}
              placeholder={t(
                'generic_content.drawer_new_generic_content.fields.tag_content.placeholder',
              )}
            />

            <div className="flex gap-4">
              <div className="flex-1">
                <Input.Base
                  name="fieldKey"
                  label={t(
                    'generic_content.drawer_new_generic_content.fields.field_key.label',
                  )}
                  placeholder={t(
                    'generic_content.drawer_new_generic_content.fields.field_key.placeholder',
                  )}
                />
              </div>

              <div className="flex-1">
                <Input.Base
                  name="fieldValue"
                  label={t(
                    'generic_content.drawer_new_generic_content.fields.field_value.label',
                  )}
                  placeholder={t(
                    'generic_content.drawer_new_generic_content.fields.field_value.placeholder',
                  )}
                />
              </div>
            </div>

            <Creatable.Sync
              name="contentType"
              isMulti
              label={t(
                'generic_content.drawer_new_generic_content.fields.content_type.label',
              )}
              placeholder={t(
                'generic_content.drawer_new_generic_content.fields.content_type.placeholder',
              )}
            />

            <Creatable.Sync
              name="relativeKeys"
              isMulti
              label={t(
                'generic_content.drawer_new_generic_content.fields.relative_keys.label',
              )}
              placeholder={t(
                'generic_content.drawer_new_generic_content.fields.relative_keys.placeholder',
              )}
            />

            <Creatable.Sync
              name="searchTags"
              isMulti
              label={t(
                'generic_content.drawer_new_generic_content.fields.search_tags.label',
              )}
              placeholder={t(
                'generic_content.drawer_new_generic_content.fields.search_tags.placeholder',
              )}
            />

            <Input.TextArea
              name="infoMessage"
              label={t(
                'generic_content.drawer_new_generic_content.fields.info_message.label',
              )}
              placeholder={t(
                'generic_content.drawer_new_generic_content.fields.info_message.placeholder',
              )}
            />
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={handleCancel}
            disabled={isCreatingGenericContent}
          >
            {t(
              'generic_content.drawer_new_generic_content.footer.close_button.label',
            )}
          </Button.Root>

          <Button.Root
            variant="primary"
            color="brand"
            onClick={hookForm.handleSubmit(handleSubmit)}
            type="submit"
            isLoading={isCreatingGenericContent}
          >
            {t(
              'generic_content.drawer_new_generic_content.footer.submit_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
